import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useAuth } from './useAuth'
import { useMaintenanceCacheBus } from './useMaintenanceCacheBus'
import { apiService, MaintenanceCacheInvalidationEvent } from '../services/api'
import { cacheDebugLogger } from '../utils/cacheDebugLogger'
import { expandStatusFilter } from '../utils/status'
import {
  MaintenanceItem,
  MaintenanceFilters,
  PaginationInfo,
  ClientCacheState,
  CacheMetadata,
  BackendCacheMetadata,
  MaintenanceRequestResponse,
  ApprovalPayloadDTO,
  MaintenanceUpdateCreateDTO,
  ReminderResponseDTO,
  StalledReportResponseDTO,
  MaintenanceRecurrenceMetadataDTO,
  RecurrenceWarningDTO
} from '../types/maintenance'

interface UseMaintenanceOptions {
  autoFetch?: boolean
  pageSize?: number
  cacheTtlSeconds?: number
  debounceMs?: number
  /** Estado inicial de filtros — evita chamadas a setFilters() no mount do consumidor */
  initialFilters?: MaintenanceFilters
  /** Página inicial — evita chamadas a setPage() no mount do consumidor */
  initialPage?: number
}

interface UseMaintenanceReturn {
  items: MaintenanceItem[]
  loading: boolean
  error: string | null
  pagination: PaginationInfo
  filters: MaintenanceFilters
  currentPage: number
  
  // Cache and refresh states
  cacheMetadata: CacheMetadata
  refreshInProgress: boolean
  lastSuccessfulFetch: Date | null
  isStale: boolean
  fallbackNotice: string | null
  
  // Actions
  fetchItems: (page?: number, filters?: MaintenanceFilters) => void
  setFilters: (filters: MaintenanceFilters) => void
  setPage: (page: number) => void
  refresh: () => void
  clearError: () => void
  
  // Computed values
  hasItems: boolean
  totalPages: number
  isFiltered: boolean
  
  // Recurrence-related helpers (feature 005-add-recurring-maintenance)
  /** Obtém metadados de recorrência de um item específico */
  getRecurrenceMetadata: (requestId: string) => MaintenanceRecurrenceMetadataDTO | undefined
  /** Verifica se um item tem recorrência ativa */
  isRecurringItem: (requestId: string) => boolean
  /** Obtém avisos de recorrência de um item específico */
  getRecurrenceWarnings: (requestId: string) => RecurrenceWarningDTO[]
  /** Lista todos os itens recorrentes */
  getRecurringItems: () => MaintenanceItem[]
}

const DEFAULT_CACHE_TTL = 60 // 60 seconds
const DEFAULT_DEBOUNCE_MS = 300 // 300 milliseconds

/**
 * Hook principal para gerenciamento de manutenções com suporte a recorrência
 *
 * Este hook gerencia o estado de manutenções incluindo dados de recorrência
 * conforme feature 005-add-recurring-maintenance. Ele consome dados do API client
 * e gerencia invalidação de cache para eventos específicos de recorrência.
 *
 * @param options - Opções de configuração do hook
 * @returns Objeto com estado, ações e helpers de recorrência
 */
export function useMaintenance(options: UseMaintenanceOptions = {}): UseMaintenanceReturn {
  const { 
    autoFetch = true, 
    pageSize = 20, 
    cacheTtlSeconds = DEFAULT_CACHE_TTL,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    initialFilters,
    initialPage
  } = options
  
  const { user, isAuthenticated } = useAuth()
  
  // Base states
  const [items, setItems] = useState<MaintenanceItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<MaintenanceFilters>(initialFilters ?? {})
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: initialPage ?? 1,
    limit: pageSize,
    total: 0,
    totalPages: 0
  })

  // Cache states
  const [cacheState, setCacheState] = useState<ClientCacheState | null>(null)
  const cacheStateRef = useRef<ClientCacheState | null>(null)

  const updateClientCacheState = useCallback(
    (updater: (prev: ClientCacheState | null) => ClientCacheState | null) => {
      setCacheState(prev => {
        const next = updater(prev)
        cacheStateRef.current = next
        return next
      })
    },
    []
  )
  const [backendMetadata, setBackendMetadata] = useState<BackendCacheMetadata | null>(null)
  const [fallbackNotice, setFallbackNotice] = useState<string | null>(null)
  
  // Refs for debounce and request tracking
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const filtersRef = useRef<MaintenanceFilters>(initialFilters ?? {})
  const paginationRef = useRef<PaginationInfo>({
    page: initialPage ?? 1,
    limit: pageSize,
    total: 0,
    totalPages: 0
  })
  /**
   * Race guard (estratégia: request-id monotónico).
   *
   * Com o live filtering (PR #210) e os chips multi-select de localização
   * (PR #215), seleções consecutivas disparam requests sobrepostos em sequência
   * rápida. Sem proteção, a resposta de um request antigo poderia resolver
   * depois de um mais recente e sobrepor os itens/paginação com dados que não
   * correspondem aos filtros ativos.
   *
   * Cada chamada a `fetchFromAPI` recebe um id estritamente crescente. No
   * resolve (sucesso, erro ou finally), se o id do request já não for o último
   * emitido, a resposta é ignorada — a lista corresponde sempre ao request MAIS
   * RECENTE iniciado.
   */
  const requestIdRef = useRef(0)
  useEffect(() => {
    filtersRef.current = filters
  }, [filters])

  useEffect(() => {
    paginationRef.current = pagination
  }, [pagination])

  // Check if cache is stale
  const isCacheStale = useCallback((cache: ClientCacheState) => {
    const now = new Date()
    const cacheAge = (now.getTime() - cache.storedAt.getTime()) / 1000
    return cacheAge >= cache.ttlSeconds
  }, [])

  // Update cache state with new data
  const updateCacheState = useCallback(
    (
      newItems: MaintenanceItem[],
      newPagination: PaginationInfo,
      newBackendMetadata: BackendCacheMetadata | null,
      filters?: MaintenanceFilters
    ) => {
      const now = new Date()
      const isFallback =
        newBackendMetadata?.fallback || newBackendMetadata?.source === 'FALLBACK'

      let ttlSeconds = cacheTtlSeconds
      if (newBackendMetadata?.ttlRemainingMs) {
        ttlSeconds = Math.ceil(newBackendMetadata.ttlRemainingMs / 1000)
      }

      cacheDebugLogger.debug('useMaintenance.updateCacheState', 'Atualizando cache local', {
        itemCount: newItems.length,
        pagination: newPagination,
        backendSource: newBackendMetadata?.source,
        backendCacheStatus: newBackendMetadata?.cacheStatus,
        backendTtlRemainingMs: newBackendMetadata?.ttlRemainingMs,
        derivedTtlSeconds: ttlSeconds,
        fallback: isFallback
      })

      console.debug('[useMaintenance] updateCacheState', {
        itemCount: newItems.length,
        pagination: newPagination,
        backendSource: newBackendMetadata?.source,
        backendCacheStatus: newBackendMetadata?.cacheStatus,
        ttlSecondsFromBackend: newBackendMetadata?.ttlRemainingMs,
        derivedTtlSeconds: ttlSeconds
      })

      updateClientCacheState(prev => {
        const previousFallbackHitCount = prev?.fallbackHitCount || 0
        const previousCacheHitCount = prev?.cacheHitCount || 0

        const newCacheState: ClientCacheState = {
          dataset: {
            items: newItems,
            pagination: newPagination
          },
          filters,
          storedAt: now,
          ttlSeconds,
          isStale: false,
          lastSuccessfulFetch: now,
          refreshInProgress: false,
          fallbackNotice: isFallback
            ? 'Dados temporariamente indisponíveis. Exibindo informações em cache.'
            : null,
          cacheHitCount: previousCacheHitCount,
          fallbackHitCount: isFallback ? previousFallbackHitCount + 1 : previousFallbackHitCount,
          preservedDatasetAgeMs: newBackendMetadata?.preservedDatasetAgeMs,
          sheetsErrorCode: newBackendMetadata?.sheetsErrorCode
        }

        cacheDebugLogger.debug(
          'useMaintenance.updateCacheState',
          'Estado de cache local atualizado',
          {
            cacheHitCount: newCacheState.cacheHitCount,
            fallbackHitCount: newCacheState.fallbackHitCount,
            fallbackNotice: newCacheState.fallbackNotice,
            ttlSeconds: newCacheState.ttlSeconds
          }
        )

        return newCacheState
      })

      setBackendMetadata(newBackendMetadata)
      setFallbackNotice(
        isFallback ? 'Dados temporariamente indisponíveis. Exibindo informações em cache.' : null
      )
    },
    [cacheTtlSeconds, updateClientCacheState]
  )

  // Fetch data from API with cache logic
  const fetchFromAPI = useCallback(async (
    page: number,
    requestFilters: MaintenanceFilters,
    forceRefresh: boolean = false
  ): Promise<void> => {
    if (!isAuthenticated) {
      return
    }

    // Marca este fetch como o request mais recente. Qualquer fetch anterior
    // (com request-id menor) terá sua resposta ignorada no resolve.
    const requestId = ++requestIdRef.current

    // Check if we have valid cache and not forcing refresh
    const currentCache = cacheStateRef.current

    console.debug('[useMaintenance] fetchFromAPI cache check', {
      page,
      filters: requestFilters,
      forceRefresh,
      hasCache: !!currentCache,
      isCacheStale: currentCache ? isCacheStale(currentCache) : 'no-cache'
    })

    // Check if cache filters match request filters
    const filtersMatch = currentCache?.filters
      ? JSON.stringify(currentCache.filters) === JSON.stringify(requestFilters)
      : JSON.stringify({}) === JSON.stringify(requestFilters);

    if (!forceRefresh && currentCache && !isCacheStale(currentCache) && filtersMatch) {
      // Cache hit - increment counter and return cached data
      updateClientCacheState(prev =>
        prev
          ? {
              ...prev,
              cacheHitCount: prev.cacheHitCount + 1
            }
          : prev
      )

      console.debug('[useMaintenance] fetchFromAPI cache hit - returning cached data', {
        page,
        filters: requestFilters,
        cacheTtlSeconds: currentCache.ttlSeconds,
        storedAt: currentCache.storedAt,
        cachedItems: currentCache.dataset.items.length,
        cachedPagination: currentCache.dataset.pagination
      })

      setItems(currentCache.dataset.items)
      setPagination(currentCache.dataset.pagination)
      setLoading(false)
      return
    }

    console.debug('[useMaintenance] fetchFromAPI cache miss or force refresh - fetching from API', {
      page,
      filters: requestFilters,
      forceRefresh,
      reason: forceRefresh ? 'force refresh requested' : (currentCache ? 'cache stale' : 'no cache')
    })

    // If we have stale cache, show it immediately while fetching fresh data
    if (!forceRefresh && currentCache && isCacheStale(currentCache)) {
      setItems(currentCache.dataset.items)
      setPagination(currentCache.dataset.pagination)
      updateClientCacheState(prev =>
        prev
          ? {
              ...prev,
              isStale: true,
              refreshInProgress: true
            }
          : prev
      )
    } else {
      setLoading(true)
      updateClientCacheState(prev =>
        prev
          ? {
              ...prev,
              refreshInProgress: true
            }
          : prev
      )
    }

    setError(null)

    try {
      // Prepare params, removing empty/undefined values
      type FetchParams = {
        page?: number
        limit?: number
        status?: string[] | string
        urgency?: string[] | string
        department?: string[] | string
        subcategory?: string[] | string
        responsibleId?: string[] | string
        storeIds?: string[] | string
        search?: string
        startDate?: string
        endDate?: string
        dateRange?: { start?: Date; end?: Date }
        [key: string]: unknown
      }
      // Spread filters but coerce dateRange.start/end from null to undefined
      // (MaintenanceFilters uses null for "cleared" state; FetchParams uses undefined)
      const { dateRange: rawDateRange, ...filtersWithoutDateRange } = requestFilters
      const params: FetchParams = {
        page,
        limit: pageSize,
        ...filtersWithoutDateRange,
        ...(rawDateRange ? {
          dateRange: {
            start: rawDateRange.start ?? undefined,
            end: rawDateRange.end ?? undefined,
          }
        } : {})
      }

      // Flatten dateRange to startDate and endDate
      if (params.dateRange) {
        if (params.dateRange.start) {
          params.startDate = params.dateRange.start.toISOString()
        }
        if (params.dateRange.end) {
          params.endDate = params.dateRange.end.toISOString()
        }
        delete params.dateRange
      }

      // Expande tokens sintéticos do filtro de status antes de serializar.
      const expanded = expandStatusFilter({
        status: Array.isArray(params.status) ? params.status : [],
        subcategory: Array.isArray(params.subcategory)
          ? (params.subcategory as ('manutencao' | 'compras')[])
          : undefined,
      })
      params.status = expanded.status
      params.subcategory = expanded.subcategory

      // Convert array filters to comma-separated strings and remove empty arrays
      if (Array.isArray(params.status)) {
        params.status = params.status.length > 0 ? params.status.join(',') : undefined
      }
      if (Array.isArray(params.urgency)) {
        params.urgency = params.urgency.length > 0 ? params.urgency.join(',') : undefined
      }
      if (Array.isArray(params.department)) {
        params.department = params.department.length > 0 ? params.department.join(',') : undefined
      }
      if (Array.isArray(params.subcategory)) {
        params.subcategory = params.subcategory.length > 0 ? params.subcategory.join(',') : undefined
      }
      if (Array.isArray(params.responsibleId)) {
        params.responsibleId = params.responsibleId.length > 0 ? params.responsibleId.join(',') : undefined
      }
      if (Array.isArray(params.storeIds)) {
        params.storeIds = params.storeIds.length > 0 ? params.storeIds.join(',') : undefined
      }

      // Remove keys with undefined or empty string values
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '') {
          delete params[key]
        }
      })

      const response = await apiService.getMaintenanceRequests(params as Parameters<typeof apiService.getMaintenanceRequests>[0])

      // Race guard: ignora resposta de um request que já não é o mais recente
      // (um request mais novo foi iniciado enquanto este estava em voo).
      if (requestId !== requestIdRef.current) {
        console.debug('[useMaintenance] fetchFromAPI resposta ignorada (request suplantado)', {
          requestId,
          latestRequestId: requestIdRef.current,
          page,
          filters: requestFilters
        })
        return
      }

      // Update items and pagination
      console.debug('[useMaintenance] fetchFromAPI response', {
        page,
        filters: requestFilters,
        forceRefresh,
        responsePagination: response.pagination,
        cacheMetadata: response.cacheMetadata
      })

      const items = response.requests || response.items || []
      
      // Garantir que a paginação tenha valores válidos
      const adjustedPagination = {
        ...response.pagination,
        limit: response.pagination.limit || pageSize,
        total: response.pagination.total || 0,
        totalPages: response.pagination.totalPages || 0
      }
      
      setItems(items)
      setPagination(adjustedPagination)
      
      // Update cache state
      updateCacheState(items, adjustedPagination, response.cacheMetadata || null, requestFilters)
    } catch (err) {
      // Race guard: erro de um request suplantado não deve poluir o estado atual.
      if (requestId !== requestIdRef.current) {
        return
      }
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch maintenance requests'
      setError(errorMessage)

      // If we have stale cache, keep showing it
      const fallbackCache = cacheStateRef.current
      if (fallbackCache) {
        setItems(fallbackCache.dataset.items)
        setPagination(fallbackCache.dataset.pagination)
        setFallbackNotice('Erro ao carregar dados. Exibindo informações em cache.')
      }
    } finally {
      // Race guard: só o request mais recente controla loading/refreshInProgress;
      // um request suplantado não pode desligar o loading de um mais novo.
      if (requestId === requestIdRef.current) {
        console.debug('[useMaintenance] fetchFromAPI finally', {
          page,
          filters: requestFilters,
          forceRefresh,
          refreshInProgress: cacheStateRef.current?.refreshInProgress,
          cacheState: cacheStateRef.current
        })

        setLoading(false)
        updateClientCacheState(prev =>
          prev
            ? {
                ...prev,
                refreshInProgress: false
              }
            : prev
        )
      }
    }
  }, [isAuthenticated, pageSize, isCacheStale, updateCacheState, updateClientCacheState])

  // Debounced fetch function
  const debouncedFetch = useCallback((
    page: number,
    requestFilters: MaintenanceFilters,
    forceRefresh: boolean = false
  ) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      fetchFromAPI(page, requestFilters, forceRefresh)
    }, debounceMs)
  }, [fetchFromAPI, debounceMs])

  // Public fetch method with debounce
  const fetchItems = useCallback((page = 1, requestFilters = filters) => {
    debouncedFetch(page, requestFilters, false)
  }, [debouncedFetch, filters])

  // Refresh method that forces cache invalidation
  const refresh = useCallback(() => {
    debouncedFetch(pagination.page, filters, true)
  }, [debouncedFetch, pagination.page, filters])

  // Set filters method
  const setFiltersHandler = useCallback((newFilters: MaintenanceFilters) => {
    setFilters(newFilters)
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, page: 1 }))
  }, [])

  // Set page method
  const setPage = useCallback((page: number) => {
    console.debug('[useMaintenance] setPage called', {
      newPage: page,
      currentPage: pagination.page,
      willForceRefresh: page !== pagination.page
    })
    
    setPagination(prev => ({ ...prev, page }))
    
    // Force refresh when page changes to ensure we get new data
    if (page !== pagination.page) {
      console.debug('[useMaintenance] forcing refresh due to page change', {
        oldPage: pagination.page,
        newPage: page
      })
      // Use setTimeout to ensure state is updated before fetching
      setTimeout(() => {
        debouncedFetch(page, filters, true) // forceRefresh = true
      }, 0)
    }
  }, [pagination.page, filters, debouncedFetch])

  // Clear error method
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Auto-fetch when filters, page, or authentication changes
  useEffect(() => {
    if (autoFetch && isAuthenticated) {
      debouncedFetch(pagination.page, filters, false)
    }
  }, [autoFetch, isAuthenticated, debouncedFetch, pagination.page, filters])

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  // Memoized computed values
  const hasItems = useMemo(() => items.length > 0, [items.length])
  const totalPages = useMemo(() => pagination.totalPages, [pagination.totalPages])
  const isFiltered = useMemo(() => {
    return !!(filters.search ||
             filters.status?.length ||
             filters.urgency?.length ||
             filters.department?.length ||
             filters.responsibleId?.length ||
             filters.storeIds?.length ||
             filters.dateRange)
  }, [filters])

  // Recurrence-related helpers (feature 005-add-recurring-maintenance)
  
  /**
   * Obtém metadados de recorrência de uma requisição específica
   * @param requestId - ID da requisição de manutenção
   * @returns Metadados de recorrência ou undefined se não for recorrente
   */
  const getRecurrenceMetadata = useCallback((requestId: string): MaintenanceRecurrenceMetadataDTO | undefined => {
    const item = items.find(item => item.id === requestId)
    return item?.recurrenceMetadata
  }, [items])

  /**
   * Verifica se uma requisição é recorrente
   * @param requestId - ID da requisição de manutenção
   * @returns true se a requisição for recorrente, false caso contrário
   */
  const isRecurringItem = useCallback((requestId: string): boolean => {
    const metadata = getRecurrenceMetadata(requestId)
    return metadata?.isRecurring ?? false
  }, [getRecurrenceMetadata])

  /**
   * Obtém avisos de recorrência de uma requisição específica
   * @param requestId - ID da requisição de manutenção
   * @returns Array de avisos de recorrência (vazio por enquanto)
   * @todo Implementar lógica para obter warnings quando disponível na API
   */
  const getRecurrenceWarnings = useCallback((_requestId: string): RecurrenceWarningDTO[] => {
    // TODO: Implementar lógica para obter warnings quando disponível na API
    // Por enquanto, retorna array vazio para compatibilidade
    return []
  }, [])

  /**
   * Filtra e retorna apenas os itens recorrentes da lista atual
   * @returns Array de itens de manutenção que são recorrentes
   */
  const getRecurringItems = useCallback((): MaintenanceItem[] => {
    return items.filter(item => item.recurrenceMetadata?.isRecurring ?? false)
  }, [items])

  const { subscribe: subscribeToCacheBus, flushPending } = useMaintenanceCacheBus()

  useEffect(() => {
    if (!isAuthenticated) {
      console.debug('[useMaintenance] cache invalidation effect skipped (not authenticated)')
      return
    }

    console.debug('[useMaintenance] cache invalidation effect init', {
      userStore: user?.store
    })

    const processInvalidationEvent = (event: MaintenanceCacheInvalidationEvent) => {
      if (!event) {
        console.debug('[useMaintenance] processInvalidationEvent called with falsy event')
        return
      }

      const currentStoreId = user?.store?.toLowerCase().trim()
      const eventStoreId = event.storeId?.toLowerCase()

      console.debug('[useMaintenance] processInvalidationEvent received', {
        reason: event.reason,
        requestId: event.requestId,
        eventStoreId,
        currentStoreId
      })

      if (eventStoreId && currentStoreId && eventStoreId !== currentStoreId) {
        console.debug('[useMaintenance] processInvalidationEvent skipped due to store mismatch')
        return
      }

      // Log específico para eventos de recorrência (feature 005-add-recurring-maintenance)
      if (event.reason === 'auto_recurrence_create' ||
          event.reason === 'manual_recurrence_override' ||
          event.reason === 'recurrence_profile_update') {
        cacheDebugLogger.debug(
          'useMaintenance.processInvalidationEvent',
          `Processando evento de recorrência: ${event.reason}`,
          {
            reason: event.reason,
            requestId: event.requestId,
            storeId: event.storeId,
            timestamp: event.timestamp
          }
        )
      }

      updateClientCacheState(prev => {
        if (!prev) {
          return prev
        }

        const nextState = {
          ...prev,
          isStale: true,
          ttlSeconds: 0,
          refreshInProgress: true,
          storedAt: prev.storedAt,
          fallbackNotice: prev.fallbackNotice
        }

        console.debug('[useMaintenance] processInvalidationEvent updateClientCacheState', {
          previous: prev,
          next: nextState
        })

        return nextState
      })

      setFallbackNotice(null)
      fetchFromAPI(paginationRef.current.page, filtersRef.current, true)
        .then(() => {
          console.debug('[useMaintenance] processInvalidationEvent triggered refresh success', {
            page: paginationRef.current.page,
            filters: filtersRef.current,
            reason: event.reason
          })
        })
        .catch(error => {
          console.error('[useMaintenance] Failed to refresh maintenance data after cache invalidation', error, {
            page: paginationRef.current.page,
            filters: filtersRef.current,
            reason: event.reason
          })
        })
    }

    console.debug('[useMaintenance] solicitando flush de eventos pendentes', {
      userStore: user?.store
    })
    const handleCacheInvalidation = (event: MaintenanceCacheInvalidationEvent) => {
      console.debug('[useMaintenance] handleCacheInvalidation listener invoked', event)
      processInvalidationEvent(event)
    }

    const unsubscribe = subscribeToCacheBus(handleCacheInvalidation)
    flushPending(user?.store)

    return () => {
      console.debug('[useMaintenance] cache invalidation effect cleanup')
      unsubscribe()
    }
  }, [fetchFromAPI, flushPending, isAuthenticated, subscribeToCacheBus, updateClientCacheState, user?.store])

  // Memoized cache metadata
  const cacheMetadata: CacheMetadata = useMemo(() => ({
    backend: backendMetadata,
    client: {
      cacheHitCount: cacheState?.cacheHitCount || 0,
      fallbackHitCount: cacheState?.fallbackHitCount || 0,
      storedAt: cacheState?.storedAt || null,
      ttlSeconds: cacheState?.ttlSeconds || cacheTtlSeconds,
      isStale: cacheState?.isStale || false,
      lastSuccessfulFetch: cacheState?.lastSuccessfulFetch || null,
      refreshInProgress: cacheState?.refreshInProgress || false
    }
  }), [backendMetadata, cacheState, cacheTtlSeconds])

  return {
    items,
    loading,
    error,
    pagination,
    filters,
    currentPage: pagination.page,
    
    // Cache and refresh states
    cacheMetadata,
    refreshInProgress: cacheState?.refreshInProgress || false,
    lastSuccessfulFetch: cacheState?.lastSuccessfulFetch || null,
    isStale: cacheState?.isStale || false,
    fallbackNotice,
    
    // Actions
    fetchItems,
    setFilters: setFiltersHandler,
    setPage,
    refresh,
    clearError,
    
    // Computed values
    hasItems,
    totalPages,
    isFiltered,
    
    // Recurrence-related helpers (feature 005-add-recurring-maintenance)
    getRecurrenceMetadata,
    isRecurringItem,
    getRecurrenceWarnings,
    getRecurringItems
  }
}

/**
 * Hook for managing a single maintenance item
 */
export function useMaintenanceItem(id?: string) {
  const [item, setItem] = useState<MaintenanceRequestResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { subscribe: subscribeToCacheBus, flushPending } = useMaintenanceCacheBus()
  const idRef = useRef<string | undefined>(id)

  useEffect(() => {
    idRef.current = id
  }, [id])

  const fetchItem = useCallback(async (itemId: string) => {
    if (!itemId) return

    setLoading(true)
    setError(null)

    try {
      const response = await apiService.getMaintenanceRequest(itemId)
      setItem(response)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao carregar requisição de manutenção'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (id) {
      fetchItem(id)
    }
  }, [fetchItem, id])

  useEffect(() => {
    if (!id) {
      return
    }

    const handleInvalidation = (event: MaintenanceCacheInvalidationEvent) => {
      if (!idRef.current) {
        return
      }

      if (event.requestId && event.requestId !== idRef.current) {
        return
      }

      cacheDebugLogger.debug(
        'useMaintenanceItem.handleInvalidation',
        'Recarregando item detalhado após evento de cache',
        {
          reason: event.reason,
          requestId: event.requestId
        }
      )

      fetchItem(idRef.current)
    }

    const unsubscribe = subscribeToCacheBus(handleInvalidation)
    flushPending()

    return () => {
      unsubscribe()
    }
  }, [fetchItem, flushPending, id, subscribeToCacheBus])

  const approveItem = useCallback(async (payload?: ApprovalPayloadDTO) => {
    if (!item?.id) return

    try {
      const updatedItem = await apiService.approveMaintenanceRequest(item.id, payload)
      setItem(updatedItem)
      return updatedItem
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao aprovar requisição'
      setError(errorMessage)
      throw err
    }
  }, [item])

  const rejectItem = useCallback(async (rejectionData: { reason: string }) => {
    if (!item?.id) return

    try {
      const updatedItem = await apiService.rejectMaintenanceRequest(item.id, rejectionData)
      setItem(updatedItem as unknown as MaintenanceRequestResponse)
      return updatedItem
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao rejeitar requisição'
      setError(errorMessage)
      throw err
    }
  }, [item])

  const addUpdate = useCallback(async (payload: MaintenanceUpdateCreateDTO) => {
    if (!item?.id) return

    try {
      const updatedItem = await apiService.postMaintenanceUpdate(item.id, payload)
      setItem(updatedItem)
      return updatedItem
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao adicionar atualização'
      setError(errorMessage)
      throw err
    }
  }, [item])

  const sendReminder = useCallback(async (payload?: { force?: boolean }): Promise<ReminderResponseDTO> => {
    if (!item?.id) throw new Error('Item não encontrado')

    try {
      const response = await apiService.remindMaintenanceRequest(item.id, payload)
      return response
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao enviar lembrete'
      setError(errorMessage)
      throw err
    }
  }, [item])

  const completeItem = useCallback(async (completionData: {
    executionDate: string
    actualCost: number
    solutionDescription: string
    responsibleTechnician: string
    rating?: number
    finalObservations?: string
    attachmentUrls?: string[]
  }) => {
    if (!item?.id) return

    try {
      const updatedItem = await apiService.completeMaintenanceRequest(item.id, completionData)
      setItem(updatedItem as unknown as MaintenanceRequestResponse)
      return updatedItem
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao concluir requisição'
      setError(errorMessage)
      throw err
    }
  }, [item])

  return {
    item,
    loading,
    error,
    fetchItem,
    approveItem,
    rejectItem,
    addUpdate,
    sendReminder,
    completeItem
  }
}

/**
 * Hook para gerenciar relatório de requisições estagnadas
 */
export function useInventoryStalledReport() {
  const [report, setReport] = useState<StalledReportResponseDTO | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchReport = useCallback(async (filters?: {
    status?: string
    days?: number
    storeId?: string
  }) => {
    setLoading(true)
    setError(null)

    try {
      const response = await apiService.getInventoryStalledReport(filters)
      setReport(response)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Falha ao carregar relatório de requisições estagnadas'
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return {
    report,
    loading,
    error,
    fetchReport,
    clearError
  }
}